import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useEndpoints } from "../../api/ApiContext";
import type { PublisherProfile } from "../../api/types";
import { CommandError } from "../../components/CommandError";
import { Button, Card, Input, QueryView, StatusPill, type Tone } from "../../components/ui";
import { formatLocal } from "../../lib/datetime";
import { useIntentKey } from "../../lib/useIntentKey";

// UX-DESIGN.md §3.0 organization profile (G-31, migration 0057):
//   GET   /v1/publisher                 publisher_reads.go:98
//   PATCH /v1/publisher {legal_name}    publisher_commands.go:444
// Only the legal name is editable, and only until identity verification
// starts (editable_fields). Entity type, address and tax id are KYC/KYB
// fields collected by verification, not here; KYC status is read-only.

const KYC: Record<PublisherProfile["kyc_status"], { tone: Tone; label: string }> = {
  not_started: { tone: "neutral", label: "Not started" },
  pending: { tone: "warning", label: "In progress" },
  cleared: { tone: "success", label: "Verified" },
  rejected: { tone: "error", label: "Couldn't be verified" },
};

export function PublisherProfileCard() {
  const ep = useEndpoints();
  const q = useQuery({ queryKey: ["publisher-profile"], queryFn: () => ep.publisher.getProfile() });
  return (
    <Card title="Organization profile" subtitle="Your legal details as the payee for this account.">
      <QueryView query={q} loadingLabel="Loading profile" lines={3}>
        {(p: PublisherProfile) => <ProfileBody p={p} />}
      </QueryView>
    </Card>
  );
}

function ProfileBody({ p }: { p: PublisherProfile }) {
  const ep = useEndpoints();
  const qc = useQueryClient();
  const intent = useIntentKey();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(p.legal_name);
  const [error, setError] = useState<string | null>(null);
  const editable = p.editable_fields.includes("legal_name");
  const m = useMutation({
    mutationFn: (n: string) => ep.publisher.updateProfile(n, { idempotencyKey: intent.keyFor(n) }),
    onSuccess: () => {
      intent.reset();
      setEditing(false);
      void qc.invalidateQueries({ queryKey: ["publisher-profile"] });
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const n = name.trim();
    if (!n) return setError("Enter your legal name.");
    if ([...n].length > 200) return setError("Use at most 200 characters.");
    setError(null);
    m.mutate(n);
  }

  const kyc = KYC[p.kyc_status] ?? { tone: "neutral" as Tone, label: p.kyc_status };
  return (
    <div className="stack">
      <dl className="definition-list">
        <dt>Legal name</dt>
        <dd>{p.legal_name}</dd>
        <dt>Identity verification</dt>
        <dd>
          <StatusPill tone={kyc.tone}>{kyc.label}</StatusPill>
        </dd>
        <dt>Payout account</dt>
        <dd>{p.payout_account.payouts_enabled ? "Payouts enabled" : p.payout_account.linked ? "Set up, not enabled yet" : "Not set up"}</dd>
        <dt>Account created</dt>
        <dd>{formatLocal(p.created_at)}</dd>
      </dl>
      {editing ? (
        <form className="stack" onSubmit={submit} noValidate>
          <div style={{ maxWidth: 480 }}>
            <Input label="Legal name" value={name} onChange={(e) => setName(e.target.value)} error={error} />
          </div>
          {m.isError ? <CommandError error={m.error} /> : null}
          <div className="row">
            <Button type="submit" loading={m.isPending}>
              Save
            </Button>
            <Button variant="ghost" onClick={() => setEditing(false)} disabled={m.isPending}>
              Cancel
            </Button>
          </div>
        </form>
      ) : editable ? (
        <div>
          <Button variant="secondary" size="sm" onClick={() => (setName(p.legal_name), setEditing(true))}>
            Edit legal name
          </Button>
        </div>
      ) : (
        <p className="xs muted">
          Your legal name is locked while identity verification is in progress or complete. Contact support to correct it.
        </p>
      )}
      <p className="xs muted">
        Entity type, address and tax details are collected during identity verification, not here.
      </p>
    </div>
  );
}
